import React, { useEffect, useState, useCallback } from 'react';

import { Stack, useMediaQuery, useTheme, Button, Box } from '@mui/material';

import { SearchGoogleMap } from './SearchGoogleMap';
import { FilterBar, FilterBarProvider } from '../../../components/FilterBar';
import { FooterBar } from '../../../components/FooterBar';
import { useSearchResults } from '../providers/SearchResultsProvider';
import { useFilteredSearchResults } from '../providers/FilteredSearchResultsProvider';
import { SpacesList } from './SpacesList';
import { List, Map } from 'react-feather';
import { CategorySelector } from './CategorySelector';
import { LoadMoreButton } from './LoadMoreButton';
import { SearchFiltersDialog } from './SearchFiltersDialog';
import { SearchFiltersButton } from './SearchFiltersButton';
import { useAuth } from '../../../contexts/AuthContext';

export const SearchResults = () => {
  const theme = useTheme();
  const lgMedia = useMediaQuery(theme.breakpoints.up('lg'));
  const smMedia = useMediaQuery(theme.breakpoints.down('md'));
  const [showMap, setShowMap] = useState(true);
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const { guest, setLoginDialogOpen } = useAuth();

  const buildMapWidth = () => {
    if (lgMedia) {
      return '33vw';
    }
    if (smMedia) {
      return '100vw';
    }

    return '50vw';
  };

  const mapStyle = {
    width: buildMapWidth(),
    height: smMedia ? 'calc(100vh - 276px)' : 'calc(100vh - 226px)',
    borderRadius: '12px',
    margin: '24px',
  };

  const showList = !smMedia || !showMap;

  useEffect(() => {
    if (smMedia) {
      setShowMap(false);
    } else {
      setShowMap(true);
    }
  }, [smMedia]);

  const {
    locationQuery,
    handleLocationQueryChange,
    handleBoundsChange,
    loading,
    hasMore,
    loadMore,
    isFetchingMore,
    viewport,
    currentBounds,
  } = useSearchResults();
  const {
    filteredSpaces,
    capacityFilterValues,
    setCapacityFilterValues,
    selectedCategory,
    setSelectedCategory,
    dateTimeFilter,
    setDateTimeFilter,
  } = useFilteredSearchResults();

  useEffect(() => {
    if (guest && hasMore && filteredSpaces.length === 0) {
      loadMore();
    }
  }, [guest, filteredSpaces.length, hasMore, loadMore]);

  const handleClickLoadMore = useCallback(() => {
    if (!guest) {
      setLoginDialogOpen(true);
      return;
    }
    loadMore();
  }, [guest, loadMore, setLoginDialogOpen]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        gap={1.5}
        sx={{
          width: '100%',
          padding: '24px',
        }}
      >
        <FilterBarProvider
          state={{
            mode: 'search',
            locationQuery,
            onChangeLocationQuery: (queryString: string | null) =>
              handleLocationQueryChange(queryString || ''),
            dateTimeFilter,
            setDateTimeFilter,
            capacityFilterValues,
            setCapacityFilterValues,
          }}
        >
          <FilterBar />
        </FilterBarProvider>
        <SearchFiltersButton setFiltersDialogOpen={setFiltersDialogOpen} />
      </Stack>
      <CategorySelector
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      {smMedia && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '10px 12px 10px 16px',
          }}
        >
          <Button
            onClick={() => setShowMap(!showMap)}
            startIcon={showMap ? <List /> : <Map />}
            sx={{
              color: 'inherit',
            }}
          >
            {showMap ? 'List' : 'Map'}
          </Button>
        </Box>
      )}
      <Stack
        direction="row"
        sx={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {showList && (
          <Stack
            justifyContent="space-between"
            sx={{
              height: 'calc(100vh - 226px)',
              padding: '24px 24px 0px 24px',
              overflowY: 'auto',
              width: {
                xs: '100vw',
                md: '50vw',
                lg: '67vw',
              },
            }}
          >
            <SpacesList loading={loading} spaces={filteredSpaces || []} />
            {hasMore && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <LoadMoreButton
                  onClick={handleClickLoadMore}
                  loading={isFetchingMore}
                />
              </Box>
            )}
            <FooterBar />
          </Stack>
        )}
        {showMap && (
          <SearchGoogleMap
            mapStyle={mapStyle}
            viewport={viewport || currentBounds}
            onBoundsChange={(bounds) => {
              handleBoundsChange(bounds);
            }}
          />
        )}
      </Stack>
      <SearchFiltersDialog
        open={filtersDialogOpen}
        onClose={() => setFiltersDialogOpen(false)}
      />
    </Box>
  );
};
